import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripDotsIcon, I } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';
import type { InvoiceBlockResponse } from '@/shared/api';
import { computeBlock, inlineRateText } from './computeBlockLine';

interface Props {
  block: InvoiceBlockResponse;
  previewNights: number;
  previewGuests: number;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Ein wiederkehrender Posten als sortierbare Zeile.
 *
 * <p>Der Griff war bis Sprint 55 `opacity-0 group-hover:opacity-100`. Auf einem Touchgerät gibt
 * es keinen Hover — die Reihenfolge liess sich dort also gar nicht ändern. Er ist jetzt
 * dauerhaft sichtbar, trägt `touch-none`, damit der Zug nicht als Scrollen gedeutet wird, und
 * ist über die Tastatur bedienbar (der `KeyboardSensor` liegt im Abschnitt).</p>
 */
export function SortableBlockRow({ block, previewNights, previewGuests, onEdit, onDelete }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const computed = computeBlock(block, previewNights, previewGuests);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'border-line relative grid grid-cols-[1fr_auto] items-center gap-3 border-b p-2 last:border-b-0',
        !block.isActive && 'opacity-60',
      )}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-ink truncate font-serif text-base">{block.label}</span>
          {!block.isActive && (
            <span className="text-ink-faint text-[11px] uppercase tracking-wider">nicht aktiv</span>
          )}
        </div>
        <div className="text-ink-soft mt-0.5 text-xs">{inlineRateText(block)}</div>
        <div className="text-ink-faint mt-1 text-xs tabular-nums">{computed.computedLabel}</div>
      </div>

      <div className="flex items-center">
        <IconButton
          label={`${block.label} verschieben`}
          className="cursor-grab touch-none active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripDotsIcon />
        </IconButton>
        <IconButton label={`${block.label} bearbeiten`} onClick={onEdit}>
          <I name="Edit" size={16} />
        </IconButton>
        <IconButton
          label={`${block.label} entfernen`}
          onClick={onDelete}
          className="hover:text-sealwax"
        >
          <I name="Close" size={16} />
        </IconButton>
      </div>
    </li>
  );
}

function IconButton({
  label,
  children,
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'text-ink-soft hover:bg-card-alt hover:text-ink focus-visible:ring-sage flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
